import React, { useContext, useEffect, useRef } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import { getText } from "../configure/utils";
import { useAuth } from "../msalAuth/AuthProvider";
import { useEnv } from "./utils";

export default function Chatbox() {
  const { language } = useContext(LanguageContext);
  const { user } = useAuth();
  const { isValid, values, error } = useEnv(["DIALOGFLOW_CX_PROJECT_ID", "DIALOGFLOW_CX_PCLM_AGENT_ID"]);

  const languageRef = useRef(language || "th");
  const emailRef = useRef(user?.username || "");

  useEffect(() => {
    languageRef.current = language || "th";
  }, [language]);

  useEffect(() => {
    emailRef.current = user?.username || "";
  }, [user?.username]);

  useEffect(() => {
    if (!isValid) return;

    const options = {
      dfMessengerSelector: "df-messenger",
      debug: false,
      lockTypes: {
        chips: true,
        buttons: true,
        list: true,
      },
      disabledOpacity: 0.55,
      secondPassDelayMs: 250,
      evtStart: "greeting",
    };

    const log = (...args) => options.debug && console.log("[DF-LOCK]", ...args);

    let destroyed = false;
    let currentStartBotIndex = 0;
    let applyScheduled = false;
    let applyQueued = false;
    let applyEmailOnce = false;

    const original = new WeakMap();

    function getMessenger() {
      return document.querySelector(options.dfMessengerSelector);
    }

    function rememberOriginal(el) {
      if (original.has(el)) return;
      original.set(el, {
        pointerEvents: el.style.pointerEvents,
        opacity: el.style.opacity,
        cursor: el.style.cursor,
        tabIndex: el.getAttribute("tabindex"),
        ariaDisabled: el.getAttribute("aria-disabled"),
        disabledProp: "disabled" in el ? el.disabled : undefined,
      });
    }

    function setDisabled(el, disabled) {
      if (!el) return;
      rememberOriginal(el);
      const orig = original.get(el);

      if (disabled) {
        el.style.pointerEvents = "none";
        el.style.opacity = String(options.disabledOpacity);
        el.style.cursor = "not-allowed";
        el.setAttribute("aria-disabled", "true");
        el.setAttribute("tabindex", "-1");
        if ("disabled" in el) el.disabled = true;
      } else {
        el.style.pointerEvents = orig.pointerEvents || "";
        el.style.opacity = orig.opacity || "";
        el.style.cursor = orig.cursor || "";
        if (orig.ariaDisabled == null) el.removeAttribute("aria-disabled");
        else el.setAttribute("aria-disabled", orig.ariaDisabled);
        if (orig.tabIndex == null) el.removeAttribute("tabindex");
        else el.setAttribute("tabindex", orig.tabIndex);
        if (orig.disabledProp !== undefined) el.disabled = orig.disabledProp;
      }
    }

    function getBotUtterances() {
      const cx = document?.querySelector("df-messenger");
      if (!cx) return [];

      let content = null;

      if (cx?.querySelector("df-messenger-chat-bubble")) {
        content = document
          ?.querySelector("df-messenger")
          ?.querySelector("df-messenger-chat-bubble")
          ?.shadowRoot?.querySelector("df-messenger-chat")
          ?.shadowRoot?.querySelector("df-messenger-message-list")
          ?.shadowRoot?.querySelectorAll(".content .bot df-messenger-utterance");
      } else {
        content = document
          ?.querySelector("df-messenger")
          ?.querySelector("df-messenger-chat")
          ?.shadowRoot?.querySelector("df-messenger-message-list")
          ?.shadowRoot?.querySelectorAll(".content .bot df-messenger-utterance");
      }

      return Array.from(content || []);
    }

    function collectButtonsFromUtterance(utterance) {
      const chipButtons = [];
      const cardButtons = [];
      const listButtons = [];

      const uRoot = utterance?.shadowRoot;
      if (!uRoot) return { chipButtons, cardButtons, listButtons };

      // chips
      if (options.lockTypes.chips) {
        const dfChips = uRoot.querySelectorAll("df-chips");
        for (const chip of dfChips) {
          const chipRoot = chip?.shadowRoot;
          if (!chipRoot) continue;
          chipButtons.push(...chipRoot.querySelectorAll("button"));
        }
      }

      // cards
      const dfCards = uRoot.querySelectorAll("df-card");
      for (const card of dfCards) {
        const cardRoot = card?.shadowRoot;
        if (!cardRoot) continue;

        // button fulfillment
        if (options.lockTypes.buttons) {
          const dfButtons = cardRoot.querySelectorAll("df-button");
          for (const dfBtn of dfButtons) {
            const bRoot = dfBtn?.shadowRoot;
            if (!bRoot) continue;
            cardButtons.push(...bRoot.querySelectorAll("button"));
          }
        }

        // list elements
        if (options.lockTypes.list) {
          const dfLists = cardRoot.querySelectorAll("df-list-element");
          for (const dfList of dfLists) {
            const lRoot = dfList?.shadowRoot;
            if (!lRoot) continue;
            listButtons.push(...lRoot.querySelectorAll("button"));
          }
        }
      }

      return { chipButtons, cardButtons, listButtons };
    }

    function applyTurnLock() {
      if (destroyed) return;

      const utterances = getBotUtterances();

      for (let idx = 0; idx < utterances.length; idx++) {
        const isCurrent = idx >= currentStartBotIndex;
        const { chipButtons, cardButtons, listButtons } = collectButtonsFromUtterance(utterances[idx]);

        for (const b of chipButtons) setDisabled(b, !isCurrent);
        for (const b of listButtons) setDisabled(b, !isCurrent);
        for (const b of cardButtons) setDisabled(b, !isCurrent);
      }
    }

    function scheduleApply() {
      if (destroyed) return;

      if (applyScheduled) {
        applyQueued = true;
        return;
      }

      applyScheduled = true;

      requestAnimationFrame(() => {
        applyScheduled = false;

        applyTurnLock();
        setTimeout(() => applyTurnLock(), options.secondPassDelayMs);

        if (applyQueued) {
          applyQueued = false;
          scheduleApply();
        }
      });
    }

    function setBoundaryBeforeNextBotResponse() {
      currentStartBotIndex = getBotUtterances().length;
      scheduleApply();
    }

    function onRequestSent(event) {
      const body = event?.detail?.data?.requestBody;

      setBoundaryBeforeNextBotResponse();

      if (body?.queryInput) body.queryInput.languageCode = languageRef.current || "th";

      const qp = body?.queryParams || (body.queryParams = {});
      const params = qp.parameters || (qp.parameters = {});
      if (applyEmailOnce && emailRef.current) {
        params.email = emailRef.current;
        applyEmailOnce = false;
      } else {
        if ("email" in params) delete params.email;
      }
    }

    function onResponseReceived(event) {
      scheduleApply();
    }

    async function onButtonClicked(event) {
      const d = event?.detail || {};
      const messenger = getMessenger();
      if (!messenger) return;

      if (d.actionLink) {
        if (d.event) {
          try {
            event.preventDefault();
          } catch {}
        }
        return;
      }

      if (d.event) {
        if (d.event === options.evtStart) applyEmailOnce = true;
        if (d.text) messenger.renderCustomText(d.text, false);
        return;
      }

      if (d.text) {
        try {
          event.preventDefault();
        } catch {}
        messenger.renderCustomText(d.text, false);
        await messenger.sendQuery?.(d.text);
      }
    }

    async function onListElementClicked(event) {
      const d = event?.detail || {};
      const messenger = getMessenger();
      if (!messenger) return;

      if (d.actionLink) {
        if (d.event) {
          try {
            event.preventDefault();
          } catch {}
        }
        return;
      }

      if (d.event) {
        if (d.title) messenger.renderCustomText(d.title, false);
        return;
      }

      if (d.title) {
        try {
          event.preventDefault();
        } catch {}
        messenger.renderCustomText(d.title, false);
        await messenger.sendQuery?.(d.title);
      }
    }

    function initializeChat(messenger) {
      if (!messenger) return;

      messenger.startNewSession?.({ retainHistory: false });
      currentStartBotIndex = 0;

      scheduleApply();

      applyEmailOnce = true;
      setTimeout(() => {
        if (destroyed) return;
        messenger.sendRequest?.("event", options.evtStart);
      }, 500);
    }

    function onMessengerLoaded() {
      scheduleApply();

      const messenger = getMessenger();
      const bubble = document.querySelector("df-messenger-chat-bubble");
      bubble?.openChat?.();

      initializeChat(messenger);
    }

    function onSessionExpired() {
      const messenger = getMessenger();
      if (!messenger) return;

      setBoundaryBeforeNextBotResponse();

      messenger.renderCustomText(getText("df_cx.timeout.message"), true);
      messenger.renderCustomCard([
        {
          type: "button",
          icon: { type: "chevron_right", color: "#FF9800" },
          mode: "blocking",
          text: getText("df_cx.timeout.button"),
          event: { event: options.evtStart },
        },
      ]);

      scheduleApply();
    }

    function onSessionEnded() {
      setBoundaryBeforeNextBotResponse();
    }

    const eventHandlers = {
      "df-messenger-loaded": onMessengerLoaded,

      "df-request-sent": onRequestSent,
      "df-response-received": onResponseReceived,

      "df-button-clicked": onButtonClicked,
      "df-list-element-clicked": onListElementClicked,

      "df-session-expired": onSessionExpired,
      "df-session-ended": onSessionEnded,
    };

    Object.entries(eventHandlers).forEach(([evt, handler]) => window.addEventListener(evt, handler));

    const messenger = getMessenger();
    if (messenger?.shadowRoot) scheduleApply();

    return () => {
      destroyed = true;
      Object.entries(eventHandlers).forEach(([evt, handler]) => window.removeEventListener(evt, handler));
    };
  }, [isValid]);

  if (!isValid) {
    console.error("PCLM chat configuration is invalid:", error);
    return null;
  }

  return (
    <df-messenger
      project-id={values.DIALOGFLOW_CX_PROJECT_ID}
      agent-id={values.DIALOGFLOW_CX_PCLM_AGENT_ID}
      allow-feedback="all"
      location="asia-southeast1"
      storage-option="none"
    >
      <df-messenger-chat chat-title="SANDEE - PCLM" chat-subtitle={getText("pclm.subtitle")} placeholder-text={getText("placeholdertext")} />
    </df-messenger>
  );
}
